"use client";

import { type BlogContentBlock } from "@/constants/blog-articles";
import { getBlogArticlesByLocale } from "@/constants/blog-articles-by-locale";
import { useLocale, useTranslations } from "@/lib/i18n/provider";
import { withLocale } from "@/lib/i18n/utils";
import { cn } from "@/lib/utils";
import { ArrowLeft, BookOpen } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import {
  Fragment,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const ARTICLE_ID_ALIASES: Record<string, string> = {
  "strong-fundamentals": "fundamentals",
};

const AUTHOR_AVATAR_BY_NAME: Record<string, string> = {
  "Guillem Oya": "/assets/images/guillem-oya.jpg",
};

const resolveArticleId = (articleId: string) =>
  ARTICLE_ID_ALIASES[articleId] ?? articleId;

const renderInlineText = (text: string): ReactNode[] => {
  const inlinePattern = /\*\*(.+?)\*\*|\*(.+?)\*/g;
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match = inlinePattern.exec(text);

  while (match) {
    const matchIndex = match.index;
    if (matchIndex > lastIndex) {
      parts.push(text.slice(lastIndex, matchIndex));
    }

    if (match[1]) {
      parts.push(
        <strong key={`bold-${matchIndex}`} className="font-semibold text-foreground">
          {match[1]}
        </strong>
      );
    } else if (match[2]) {
      parts.push(
        <em key={`italic-${matchIndex}`} className="italic text-foreground">
          {match[2]}
        </em>
      );
    }

    lastIndex = matchIndex + match[0].length;
    match = inlinePattern.exec(text);
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

const renderRichText = (text: string): ReactNode[] => {
  const lines = text.split("\n");
  return lines.map((line, index) => (
    <Fragment key={`line-${index}`}>
      {renderInlineText(line)}
      {index < lines.length - 1 ? <br /> : null}
    </Fragment>
  ));
};

const FormulaFraction = ({
  numerator,
  denominator,
}: {
  numerator: ReactNode;
  denominator: ReactNode;
}) => (
  <span className="mx-1 inline-flex flex-col items-center align-middle py-1 leading-tight">
    <span className="px-1">{numerator}</span>
    <span className="my-1 block h-px min-w-[5.5ch] w-full bg-current" />
    <span className="px-1">{denominator}</span>
  </span>
);

const normalizeFormulaSpacing = (line: string) =>
  line
    .replace(/\s*=\s*/g, " = ")
    .replace(/\s*\*\s*/g, " × ")
    .replace(/\s*×\s*/g, " × ")
    .replace(/\s+/g, " ")
    .trim();

const FORMULA_LITERAL_SLASH_TOKEN = "__FORMULA_LITERAL_SLASH__";

const escapeFormulaLiteralSlashes = (line: string) =>
  line.replace(/\\\//g, FORMULA_LITERAL_SLASH_TOKEN);

const unescapeFormulaLiteralSlashes = (text: string) =>
  text.replaceAll(FORMULA_LITERAL_SLASH_TOKEN, "/");

const renderFormulaExpression = (expression: string, keyPrefix: string): ReactNode => {
  const fractionOperator = " / ";
  const fractionOperatorCount = (expression.match(/ \/ /g) ?? []).length;

  if (fractionOperatorCount === 1) {
    const [numeratorRaw, denominatorRaw] = expression.split(fractionOperator);
    const numerator = numeratorRaw?.trim() ?? "";
    const denominatorWithTail = denominatorRaw?.trim() ?? "";

    const multiplicationOperator = " × ";
    const multiplicationIndex = denominatorWithTail.indexOf(multiplicationOperator);
    const denominator =
      multiplicationIndex === -1
        ? denominatorWithTail
        : denominatorWithTail.slice(0, multiplicationIndex).trim();
    const denominatorTail =
      multiplicationIndex === -1
        ? ""
        : denominatorWithTail.slice(multiplicationIndex).trim();

    if (numerator && denominator) {
      return (
        <>
          <FormulaFraction
            numerator={<>{renderInlineText(unescapeFormulaLiteralSlashes(numerator))}</>}
            denominator={<>{renderInlineText(unescapeFormulaLiteralSlashes(denominator))}</>}
          />
          {denominatorTail ? (
            <span className="ml-1 whitespace-nowrap">
              {renderInlineText(unescapeFormulaLiteralSlashes(denominatorTail))}
            </span>
          ) : null}
        </>
      );
    }
  }

  return (
    <Fragment key={`${keyPrefix}-text`}>
      {renderInlineText(unescapeFormulaLiteralSlashes(expression))}
    </Fragment>
  );
};

const renderFormulaText = (text: string): ReactNode[] => {
  const lines = text.split("\n");
  return lines.map((rawLine, index) => {
    const line = normalizeFormulaSpacing(escapeFormulaLiteralSlashes(rawLine));
    const equalIndex = line.indexOf(" = ");

    let content: ReactNode;
    if (equalIndex !== -1) {
      const left = line.slice(0, equalIndex).trim();
      const right = line.slice(equalIndex + 3).trim();
      content = (
        <>
          {renderInlineText(unescapeFormulaLiteralSlashes(left))}
          {" = "}
          {renderFormulaExpression(right, `formula-${index}-rhs`)}
        </>
      );
    } else {
      content = renderFormulaExpression(line, `formula-${index}`);
    }

    return (
      <Fragment key={`formula-line-${index}`}>
        {content}
        {index < lines.length - 1 ? <br /> : null}
      </Fragment>
    );
  });
};

const hasRenderableContent = (block: BlogContentBlock): boolean => {
  if (
    block.type === "paragraph" ||
    block.type === "heading" ||
    block.type === "formula"
  ) {
    return block.text.trim().length > 0;
  }

  return block.items.some((item) => item.trim().length > 0);
};

const renderBlock = (
  block: BlogContentBlock,
  index: number,
  blocks: BlogContentBlock[]
) => {
  const hasAnyContentAbove = blocks
    .slice(0, index)
    .some((item) => hasRenderableContent(item));
  const blockSpacingClass = hasAnyContentAbove ? "mt-4" : "";

  if (block.type === "heading") {
    const hasNonHeadingContentAbove = blocks
      .slice(0, index)
      .some((item) => item.type !== "heading" && hasRenderableContent(item));

    const headingSpacingClass =
      hasNonHeadingContentAbove || !hasAnyContentAbove ? "mt-6" : blockSpacingClass;

    return (
      <h2
        key={`${block.type}-${index}`}
        className={`text-2xl md:text-3xl font-semibold text-foreground ${headingSpacingClass}`}
      >
        {renderRichText(block.text)}
      </h2>
    );
  }

  if (block.type === "list") {
    const ListTag = block.ordered ? "ol" : "ul";
    return (
      <ListTag
        key={`${block.type}-${index}`}
        className={`${block.ordered ? "list-decimal" : "list-disc"} ${blockSpacingClass} pl-8 md:pl-10 space-y-2 text-sm md:text-base leading-7 text-foreground/90`}
      >
        {block.items.map((item) => (
          <li key={item}>
            {block.variant === "split-title" ? (
              (() => {
                const [title, ...descriptionParts] = item.split("\n");
                const description = descriptionParts.join("\n");
                return (
                  <div>
                    <h3 className="text-lg md:text-xl font-semibold text-foreground">
                      {renderRichText(title)}
                    </h3>
                    {description ? (
                      <p className="mt-1 text-sm md:text-base leading-7 text-foreground/90">
                        {renderRichText(description)}
                      </p>
                    ) : null}
                  </div>
                );
              })()
            ) : (
              <span>{renderRichText(item)}</span>
            )}
          </li>
        ))}
      </ListTag>
    );
  }

  if (block.type === "formula") {
    return (
      <div
        key={`${block.type}-${index}`}
        className={`${blockSpacingClass} text-left font-serif text-lg leading-relaxed text-foreground md:text-xl`}
      >
        <div className="overflow-x-auto overflow-y-visible py-1">
          <div className="min-w-fit">{renderFormulaText(block.text)}</div>
        </div>
      </div>
    );
  }

  return (
    <p
      key={`${block.type}-${index}`}
      className={`${blockSpacingClass} text-sm md:max-w-6xl md:text-base leading-7 text-foreground/90`}
    >
      {renderRichText(block.text)}
    </p>
  );
};

const BlogReader = ({
  articleId,
  backHref,
  backLabel,
}: {
  articleId?: string;
  backHref?: string;
  backLabel?: string;
}) => {
  const locale = useLocale();
  const t = useTranslations();
  const articles = useMemo(() => getBlogArticlesByLocale(locale), [locale]);
  const [selectedArticleId, setSelectedArticleId] = useState("");
  const [isArticleSelectionReady, setIsArticleSelectionReady] = useState(false);
  const [isMobileListOpen, setIsMobileListOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(72);
  const [desktopBottomInset, setDesktopBottomInset] = useState(0);
  const [imageLoadErrors, setImageLoadErrors] = useState<Record<string, true>>({});
  const desktopArticleContentRef = useRef<HTMLElement | null>(null);
  const mobileArticleContentRef = useRef<HTMLElement | null>(null);
  const hasInitializedArticleSelectionRef = useRef(false);
  const previousArticleIdRef = useRef<string>("");
  const previousLocaleRef = useRef(locale);

  const selectedArticle = useMemo(
    () =>
      articles.find(
        (article) => article.id === resolveArticleId(articleId ?? selectedArticleId)
      ) ??
      articles[0],
    [articleId, articles, selectedArticleId]
  );

  const getVisibleArticleElement = useCallback((): HTMLElement | null => {
    const desktopElement = desktopArticleContentRef.current;
    const mobileElement = mobileArticleContentRef.current;

    if (typeof window === "undefined") {
      return desktopElement ?? mobileElement;
    }

    const isDesktopViewport = window.matchMedia("(min-width: 1024px)").matches;
    const preferredElement = isDesktopViewport ? desktopElement : mobileElement;
    if (preferredElement) {
      return preferredElement;
    }

    return (
      [desktopElement, mobileElement].find(
        (element) => element && element.offsetParent !== null
      ) ?? null
    );
  }, []);

  const scrollToArticleTop = useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      if (typeof window === "undefined") {
        return;
      }

      const visibleArticleElement = getVisibleArticleElement();
      if (!visibleArticleElement) {
        return;
      }

      const articleTop = visibleArticleElement.getBoundingClientRect().top;
      const targetTop = window.scrollY + articleTop - Math.max(headerHeight, 0) - 16;
      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior,
      });
    },
    [getVisibleArticleElement, headerHeight]
  );

  useEffect(() => {
    if (articleId) return;
    if (!articles.length) {
      setSelectedArticleId("");
      setIsArticleSelectionReady(false);
      hasInitializedArticleSelectionRef.current = false;
      return;
    }

    let articleIdFromUrl = "";

    if (typeof window !== "undefined") {
      const articleParam = new URLSearchParams(window.location.search).get("article");
      if (articleParam) {
        const normalizedArticleId = resolveArticleId(articleParam);
        const exists = articles.some((article) => article.id === normalizedArticleId);
        if (exists) {
          articleIdFromUrl = normalizedArticleId;
        }
      }
    }

    setSelectedArticleId((currentId) => {
      if (!hasInitializedArticleSelectionRef.current && articleIdFromUrl) {
        return articleIdFromUrl;
      }

      if (!currentId && articleIdFromUrl) {
        return articleIdFromUrl;
      }

      return articles.some((article) => article.id === currentId)
        ? currentId
        : articles[0].id;
    });

    hasInitializedArticleSelectionRef.current = true;
    setIsArticleSelectionReady(true);
  }, [articleId, articles]);

  useEffect(() => {
    if (articleId) return;
    if (
      typeof window === "undefined" ||
      !isArticleSelectionReady ||
      !selectedArticle?.id
    ) {
      return;
    }

    const url = new URL(window.location.href);
    const currentArticleId = url.searchParams.get("article");
    const normalizedCurrentArticleId = resolveArticleId(currentArticleId ?? "");
    if (normalizedCurrentArticleId === selectedArticle.id) {
      if (currentArticleId !== selectedArticle.id) {
        url.searchParams.set("article", selectedArticle.id);
        window.history.replaceState(window.history.state, "", url.toString());
      }
      return;
    }

    url.searchParams.set("article", selectedArticle.id);
    window.history.replaceState(window.history.state, "", url.toString());
  }, [articleId, isArticleSelectionReady, selectedArticle?.id]);

  useEffect(() => {
    if (!selectedArticle || typeof document === "undefined") return;
    document.title = `${selectedArticle.title} - Alpha Strategy`;
  }, [selectedArticle]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const header = document.querySelector("header");
    if (!header) {
      return;
    }

    const updateHeight = () => {
      const nextHeight = Math.round(header.getBoundingClientRect().height);
      if (nextHeight > 0) {
        setHeaderHeight(nextHeight);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateHeight)
        : null;
    observer?.observe(header);

    return () => {
      window.removeEventListener("resize", updateHeight);
      observer?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !isMobileListOpen) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isMobileListOpen]);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !isArticleSelectionReady ||
      !selectedArticle?.id
    ) {
      return;
    }

    const hasLocaleChanged = previousLocaleRef.current !== locale;
    previousLocaleRef.current = locale;
    if (hasLocaleChanged) {
      previousArticleIdRef.current = selectedArticle.id;
      return;
    }

    const previousArticleId = previousArticleIdRef.current;
    const hasChangedArticle =
      previousArticleId.length > 0 && previousArticleId !== selectedArticle.id;

    previousArticleIdRef.current = selectedArticle.id;

    if (!hasChangedArticle) {
      return;
    }

    window.requestAnimationFrame(() => {
      scrollToArticleTop("smooth");
    });
  }, [isArticleSelectionReady, locale, scrollToArticleTop, selectedArticle?.id]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const footer = document.querySelector("footer");
    if (!footer) {
      return;
    }

    let rafId = 0;

    const updateBottomInset = () => {
      rafId = 0;
      const footerTop = footer.getBoundingClientRect().top;
      const nextInset = Math.max(window.innerHeight - footerTop, 0);
      setDesktopBottomInset((prevInset) =>
        Math.abs(prevInset - nextInset) < 1 ? prevInset : nextInset
      );
    };

    const scheduleUpdate = () => {
      if (rafId) {
        return;
      }
      rafId = window.requestAnimationFrame(updateBottomInset);
    };

    scheduleUpdate();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleUpdate)
        : null;
    observer?.observe(footer);

    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      observer?.disconnect();
    };
  }, []);

  if (!selectedArticle) {
    return null;
  }

  const sidebarTopValue = `${Math.max(headerHeight, 0)}px`;
  const mobilePanelWidth = "min(82vw, 320px)";

  const ArticleList = ({ isMobile = false }: { isMobile?: boolean }) => (
    <ul className="pb-3 md:pb-4">
      {articles.map((article) => {
        const isActive = article.id === selectedArticle.id;
        return (
          <li key={article.id}>
            <button
              type="button"
              onClick={() => {
                if (article.id === selectedArticle.id) {
                  return;
                }
                setSelectedArticleId(article.id);
                if (isMobile) {
                  setIsMobileListOpen(false);
                }
              }}
              className={`w-full border-l-[3px] px-5 py-4 text-left transition-colors md:px-6 ${
                isActive
                  ? "border-border/70 bg-muted/20 text-foreground"
                  : "border-transparent text-foreground/80 hover:border-foreground/25 hover:bg-muted/15 hover:text-foreground"
              }`}
            >
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                {article.readTime}
              </p>
              <p
                className={`mt-2 text-sm leading-6 ${
                  isActive ? "font-semibold" : "font-medium"
                }`}
              >
                {article.title}
              </p>
            </button>
          </li>
        );
      })}
    </ul>
  );

  const renderArticleContent = (
    articleContentRef: RefObject<HTMLElement | null>
  ) => (
    <section ref={articleContentRef} className="min-w-0">
      <article className="px-6 py-8 md:px-10 md:py-12 lg:px-14">
        <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
          <span className="inline-flex items-center gap-2.5">
            <span className="inline-flex items-center gap-3">
              {AUTHOR_AVATAR_BY_NAME[selectedArticle.author] ? (
                <Image
                  src={AUTHOR_AVATAR_BY_NAME[selectedArticle.author]}
                  alt={selectedArticle.author}
                  width={32}
                  height={32}
                  unoptimized
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : null}
              <span>{selectedArticle.author}</span>
            </span>
            <span aria-hidden className="h-3 w-px bg-muted-foreground/55" />
            <span>{selectedArticle.readTime}</span>
          </span>
        </p>
        <h1 className="mt-3 text-3xl font-semibold leading-tight text-foreground md:text-5xl">
          {selectedArticle.title}
        </h1>

        {selectedArticle.coverImageUrl &&
        !imageLoadErrors[selectedArticle.coverImageUrl] ? (
          <figure className="mt-5 w-full overflow-hidden rounded-xl border border-border/60 bg-muted/10 md:max-w-2xl">
            <Image
              src={selectedArticle.coverImageUrl}
              alt={selectedArticle.coverImageAlt ?? selectedArticle.title}
              width={1600}
              height={900}
              className="h-auto w-full object-cover"
              sizes="(max-width: 768px) 100vw, (max-width: 1280px) 60vw, 42rem"
              priority
              onError={() => {
                setImageLoadErrors((previous) => ({
                  ...previous,
                  [selectedArticle.coverImageUrl as string]: true,
                }));
              }}
            />
          </figure>
        ) : null}

        <div>
          {selectedArticle.blocks.map((block, index, blocks) =>
            renderBlock(block, index, blocks)
          )}
        </div>
      </article>
    </section>
  );

  if (articleId && backHref) {
    return (
      <section className="mx-auto w-full max-w-[1440px] pb-12 pt-6 sm:pt-12">
        <Link
          href={withLocale(backHref, locale)}
          className="flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground hover:underline"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          {backLabel ?? t("blog.backToIndex")}
        </Link>
        <article className="mt-5 max-w-4xl">
          <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
            <span className="inline-flex items-center gap-2.5">
              <span className="inline-flex items-center gap-3">
                {AUTHOR_AVATAR_BY_NAME[selectedArticle.author] ? (
                  <Image
                    src={AUTHOR_AVATAR_BY_NAME[selectedArticle.author]}
                    alt={selectedArticle.author}
                    width={32}
                    height={32}
                    unoptimized
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : null}
                <span>{selectedArticle.author}</span>
              </span>
              <span aria-hidden className="h-3 w-px bg-muted-foreground/55" />
              <span>{selectedArticle.readTime}</span>
            </span>
          </p>
          <h1 className="mt-3 text-3xl font-semibold leading-tight text-foreground md:text-5xl">
            {selectedArticle.title}
          </h1>
          {selectedArticle.coverImageUrl && !imageLoadErrors[selectedArticle.coverImageUrl] ? (
            <figure className="mt-5 w-full overflow-hidden rounded-xl border border-border/60 bg-muted/10 md:max-w-2xl">
              <Image
                src={selectedArticle.coverImageUrl}
                alt={selectedArticle.coverImageAlt ?? selectedArticle.title}
                width={1600}
                height={900}
                className="h-auto w-full object-cover"
                sizes="(max-width: 768px) 100vw, (max-width: 1280px) 60vw, 42rem"
                priority
                onError={() => {
                  setImageLoadErrors((previous) => ({
                    ...previous,
                    [selectedArticle.coverImageUrl as string]: true,
                  }));
                }}
              />
            </figure>
          ) : null}
          <div className="mt-6">
            {selectedArticle.blocks.map((block, index, blocks) =>
              renderBlock(block, index, blocks)
            )}
          </div>
        </article>
      </section>
    );
  }

  return (
    <section
      className="relative w-screen pb-4 md:pb-6"
      style={{
        marginLeft: "calc(50% - 50vw)",
        marginRight: "calc(50% - 50vw)",
        marginTop:
          "calc(-1 * min(24px, var(--user-navbar-total-height)))",
      }}
    >
      <div className="hidden lg:grid lg:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="border-r border-border" />
        {renderArticleContent(desktopArticleContentRef)}
      </div>

      <aside
        className="fixed left-0 z-[10] hidden w-[300px] border-r border-border bg-background lg:block"
        style={{
          top: sidebarTopValue,
          bottom: `${desktopBottomInset}px`,
        }}
      >
        <nav aria-label="Articles" className="h-full overflow-y-auto no-scrollbar">
          <ArticleList />
        </nav>
      </aside>

      <button
        type="button"
        onClick={() => setIsMobileListOpen((prev) => !prev)}
        className={`fixed right-4 z-[130] flex h-12 w-12 items-center justify-center rounded-full border border-transparent bg-black text-white shadow-[0_12px_30px_-18px_rgba(0,0,0,0.35)] transition hover:bg-black/90 hover:shadow-[0_14px_36px_-18px_rgba(0,0,0,0.45)] dark:bg-white dark:text-black dark:shadow-[0_12px_30px_-18px_rgba(255,255,255,0.35)] dark:hover:bg-white/90 dark:hover:shadow-[0_14px_36px_-18px_rgba(255,255,255,0.45)] lg:hidden ${
          isMobileListOpen ? "pointer-events-none opacity-0" : ""
        }`}
        aria-label={isMobileListOpen ? "Hide article list" : "Show article list"}
        aria-expanded={isMobileListOpen}
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}
      >
        <BookOpen className="h-5 w-5" aria-hidden />
      </button>

      <button
        type="button"
        onClick={() => setIsMobileListOpen(false)}
        className={cn(
          "fixed inset-0 z-[110] bg-black/30 transition-opacity lg:hidden",
          isMobileListOpen ? "opacity-100" : "pointer-events-none opacity-0"
        )}
        aria-hidden={!isMobileListOpen}
        tabIndex={isMobileListOpen ? 0 : -1}
      />

      <aside
        className={cn(
          "fixed left-0 top-0 z-[120] h-[100dvh] border-r border-border bg-background transition-transform duration-300 ease-&lsqb;cubic-bezier(0.16,1,0.3,1)&rsqb; lg:hidden",
          isMobileListOpen ? "translate-x-0" : "-translate-x-full"
        )}
        style={{
          width: mobilePanelWidth,
        }}
      >
        <nav aria-label="Articles" className="h-full overflow-y-auto no-scrollbar">
          <ArticleList isMobile />
        </nav>
      </aside>

      <div className="lg:hidden">{renderArticleContent(mobileArticleContentRef)}</div>
    </section>
  );
};

export default BlogReader;
